import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { ClassroomManagementCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const ProblemIdentificationStep: React.FC<
  StepComponentProps<ClassroomManagementCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [showCustomSubcategory, setShowCustomSubcategory] = useState(false);

  const problemCategoryOptions = [
    {
      id: "lesson_clarity",
      emoji: "📖",
      label: t("classroomManagement.problemCategory.lessonClarity"),
      desc: t("classroomManagement.problemCategory.lessonClarityDesc"),
      subcategories: [
        "unclear_expectations",
        "inappropriate_difficulty_level",
        "long_complex_explanations",
        "insufficient_materials",
      ],
    },
    {
      id: "teacher_student_dynamics",
      emoji: "🤝",
      label: t("classroomManagement.problemCategory.teacherStudentDynamics"),
      desc: t("classroomManagement.problemCategory.teacherStudentDynamicsDesc"),
      subcategories: [
        "lack_of_trust",
        "communication_issues",
        "inconsistent_expectations",
        "limited_student_voice",
      ],
    },
    {
      id: "student_behavior",
      emoji: "⚡",
      label: t("classroomManagement.problemCategory.studentBehavior"),
      desc: t("classroomManagement.problemCategory.studentBehaviorDesc"),
      subcategories: [
        "talking_without_permission",
        "disruptive_behavior",
        "attention_seeking",
        "defiance",
      ],
    },
    {
      id: "physical_space",
      emoji: "🏫",
      label: t("classroomManagement.problemCategory.physicalSpace"),
      desc: t("classroomManagement.problemCategory.physicalSpaceDesc"),
      subcategories: [
        "seating_arrangement",
        "noise_level",
        "space_utilization",
        "equipment_issues",
      ],
    },
  ];

  const getSubcategoryOptions = (categoryId: string) => {
    const category = problemCategoryOptions.find(
      (cat) => cat.id === categoryId,
    );
    if (!category) return [];

    const tt = t;

    return category.subcategories.map((sub) => ({
      id: sub,
      label: tt(`classroomManagement.problemSubcategory.${sub}`),
      desc: tt(`classroomManagement.problemSubcategory.${sub}Desc`),
    }));
  };

  const handleCategorySelect = (categoryId: string) => {
    onDataChange({
      problemCategory: categoryId,
      problemSubcategory: undefined, // Reset subcategory when category changes
    });
  };

  const handleSubcategorySelect = (subcategoryId: string) => {
    onDataChange({ problemSubcategory: subcategoryId });
  };

  const handleCustomCategoryToggle = () => {
    const currentCategory = data.problemCategory;
    const hasCustomCategory =
      currentCategory &&
      !problemCategoryOptions.some((option) => option.id === currentCategory);

    if (hasCustomCategory || showCustomCategory) {
      onDataChange({
        problemCategory: undefined,
        problemSubcategory: undefined,
      });
      setShowCustomCategory(false);
    } else {
      setShowCustomCategory(true);
    }
  };

  const handleCustomCategoryChange = (value: string) => {
    if (value.trim()) {
      onDataChange({
        problemCategory: value.trim(),
        problemSubcategory: undefined,
      });
    } else {
      onDataChange({
        problemCategory: undefined,
        problemSubcategory: undefined,
      });
    }
  };

  const handleCustomSubcategoryToggle = () => {
    const currentSubcategory = data.problemSubcategory;
    const selectedCategory = problemCategoryOptions.find(
      (cat) => cat.id === data.problemCategory,
    );
    const hasCustomSubcategory =
      currentSubcategory &&
      selectedCategory &&
      !selectedCategory.subcategories.includes(currentSubcategory);

    if (hasCustomSubcategory || showCustomSubcategory) {
      onDataChange({ problemSubcategory: undefined });
      setShowCustomSubcategory(false);
    } else {
      setShowCustomSubcategory(true);
    }
  };

  const handleCustomSubcategoryChange = (value: string) => {
    if (value.trim()) {
      onDataChange({ problemSubcategory: value.trim() });
    } else {
      onDataChange({ problemSubcategory: undefined });
    }
  };

  const isCustomCategorySelected = () => {
    const currentCategory = data.problemCategory;
    return (
      showCustomCategory ||
      (currentCategory &&
        !problemCategoryOptions.some((option) => option.id === currentCategory))
    );
  };

  const isCustomSubcategorySelected = () => {
    const currentSubcategory = data.problemSubcategory;
    const selectedCategory = problemCategoryOptions.find(
      (cat) => cat.id === data.problemCategory,
    );
    return (
      showCustomSubcategory ||
      (currentSubcategory &&
        selectedCategory &&
        !selectedCategory.subcategories.includes(currentSubcategory))
    );
  };

  const getCustomCategoryValue = () => {
    const currentCategory = data.problemCategory;
    if (
      currentCategory &&
      !problemCategoryOptions.some((option) => option.id === currentCategory)
    ) {
      return currentCategory;
    }
    return "";
  };

  const getCustomSubcategoryValue = () => {
    const currentSubcategory = data.problemSubcategory;
    const selectedCategory = problemCategoryOptions.find(
      (cat) => cat.id === data.problemCategory,
    );
    if (
      currentSubcategory &&
      selectedCategory &&
      !selectedCategory.subcategories.includes(currentSubcategory)
    ) {
      return currentSubcategory;
    }
    return "";
  };

  const subcategoryOptions = data.problemCategory
    ? getSubcategoryOptions(data.problemCategory)
    : [];

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("classroomManagement.steps.problemIdentification.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("classroomManagement.steps.problemIdentification.description")}
        </p>
      </div>

      {/* Problem Category */}
      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("classroomManagement.form.problemCategory")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("classroomManagement.form.problemCategoryDesc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {problemCategoryOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.problemCategory === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleCategorySelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.problemCategory === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.problemCategory === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Category Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomCategorySelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomCategoryToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  isCustomCategorySelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomCategorySelected() && (
                  <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("classroomManagement.problemCategory.other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("classroomManagement.problemCategory.otherDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Category Input */}
        {isCustomCategorySelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("classroomManagement.form.customCategory")}
            </label>
            <input
              type="text"
              value={getCustomCategoryValue()}
              onChange={(e) => handleCustomCategoryChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t(
                "classroomManagement.form.customCategoryPlaceholder",
              )}
            />
          </div>
        )}
      </div>

      {/* Problem Subcategory - Only show if a category is selected */}
      {data.problemCategory && subcategoryOptions.length > 0 && (
        <div>
          <h3
            className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
          >
            {t("classroomManagement.form.problemSubcategory")}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t("classroomManagement.form.problemSubcategoryDesc")}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {subcategoryOptions.map((option) => (
              <div
                key={option.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                  data.problemSubcategory === option.id
                    ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
                onClick={() => handleSubcategorySelect(option.id)}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                      data.problemSubcategory === option.id
                        ? `border-${themeColor}-500 bg-${themeColor}-500`
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                  >
                    {data.problemSubcategory === option.id && (
                      <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {option.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Custom Subcategory Option */}
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isCustomSubcategorySelected()
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={handleCustomSubcategoryToggle}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    isCustomSubcategorySelected()
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isCustomSubcategorySelected() && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">✏️</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {t("classroomManagement.problemSubcategory.other")}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t("classroomManagement.problemSubcategory.otherDesc")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Custom Subcategory Input */}
          {isCustomSubcategorySelected() && (
            <div className="mt-4">
              <label
                className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
              >
                {t("classroomManagement.form.customSubcategory")}
              </label>
              <input
                type="text"
                value={getCustomSubcategoryValue()}
                onChange={(e) => handleCustomSubcategoryChange(e.target.value)}
                className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
                placeholder={t(
                  "classroomManagement.form.customSubcategoryPlaceholder",
                )}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
